import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';

import {
  ISelectedOption,
  defaultEmptySelectedOption,
} from '../../../../constants';

interface ICustomQuizSubTopic {
  id: string;
  name: string;
  paper: string;
}

interface AddNewSubtopicToQuizModalProps {
  open: boolean;
  onClose: () => void;
  subTopics: ISelectedOption[];
  defaultSubTopicId: string;
  onSubmit: (quizSubTopic: ICustomQuizSubTopic) => void;
}

const label: TypographyProps = {
  variant: 'subtitle2',
  fontWeight: 600,
  sx: { mb: '2px' },
};

const renderInput = (params: AutocompleteRenderInputParams) => (
  <TextField {...params} placeholder="Select" />
);

const AddNewSubtopicToQuizModal: React.FC<AddNewSubtopicToQuizModalProps> = ({
  open,
  onClose,
  subTopics,
  onSubmit,
  defaultSubTopicId,
}) => {
  // State
  const [papers, setPapers] = useState<ISelectedOption[]>([]);
  const [selectedSubTopic, setSubTopic] = useState<ISelectedOption>(
    defaultEmptySelectedOption
  );

  const [selectedPaper, setPaper] = useState<ISelectedOption>(
    defaultEmptySelectedOption
  );

  useEffect(() => {
    if (subTopics && subTopics.length) {
      const selectedSubTopicOption =
        subTopics.find((subTopic) => subTopic.value === defaultSubTopicId) ||
        subTopics[0];

      setSubTopic(selectedSubTopicOption);
      setPapers(selectedSubTopicOption.papers);
    }
  }, []);

  const onSubTopicChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption
  ) => {
    setSubTopic(value);
  };

  const onPaperChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption
  ) => {
    setPaper(value);
  };

  const handleSubmit = () => {
    onSubmit({
      id: selectedSubTopic.value,
      name: selectedSubTopic.label,
      paper: selectedPaper.value,
    });

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { minWidth: '400px' } }}
    >
      <DialogTitle>Add New Subtopic</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography {...label}>SubTopic *</Typography>
            <Autocomplete
              fullWidth
              size="small"
              disableClearable
              renderInput={renderInput}
              onChange={onSubTopicChange}
              options={subTopics}
              value={selectedSubTopic}
              readOnly={subTopics.length === 1}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography {...label}>Paper *</Typography>
            <Autocomplete
              fullWidth
              size="small"
              disableClearable
              renderInput={renderInput}
              onChange={onPaperChange}
              options={papers}
              value={selectedPaper}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '16px 28px' }}>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            selectedSubTopic === defaultEmptySelectedOption ||
            selectedPaper === defaultEmptySelectedOption
          }
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewSubtopicToQuizModal;
